import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import {
  activateApartmentByAdmin,
  ApartmentAdmin,
  ApartmentCalendarInfo,
  deactivateApartmentByAdmin,
  getApartmentsByManager,
  getAllManagers,
  getUserById,
  impersonateUser,
  ManagerAdmin,
  UserAdmin,
} from '../api.admin';
import { syncCalendar, CalendarSyncResult } from '../api.calendar';
import AdminPasswordResetModal from '../components/AdminPasswordResetModal';
import { adminPasswordResetText, fillTemplate } from '../i18n/pages/adminPasswordReset';
import { useLang } from '../i18n/LanguageContext';

const getToken = () => localStorage.getItem('token') || '';

const fmtDate = (iso?: string) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('cs-CZ', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

type FeatureBadgeProps = { label: string; active: boolean };
const FeatureBadge: React.FC<FeatureBadgeProps> = ({ label, active }) => (
  <span style={{
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '2px 8px',
    borderRadius: 99,
    fontSize: '0.72rem',
    fontWeight: 600,
    background: active ? '#c6f6d5' : '#e2e8f0',
    color: active ? '#22543d' : '#718096',
  }}>
    <span style={{ fontSize: '0.65rem' }}>{active ? '●' : '○'}</span>
    {label}
  </span>
);

type StatPillProps = { label: string; value: number | string };
const StatPill: React.FC<StatPillProps> = ({ label, value }) => (
  <div style={{ textAlign: 'center', minWidth: 48 }}>
    <div style={{ fontWeight: 700, fontSize: '1.1rem', color: '#2d3748', lineHeight: 1 }}>{value}</div>
    <div style={{ fontSize: '0.65rem', color: '#a0aec0', marginTop: 2 }}>{label}</div>
  </div>
);

type ApartmentCardProps = {
  apt: ApartmentAdmin;
  returnTo: string;
  onActivate: (id: number) => void;
  onDeactivate: (id: number) => void;
};
const ApartmentCard: React.FC<ApartmentCardProps> = ({ apt, returnTo, onActivate, onDeactivate }) => {
  const [syncingId, setSyncingId] = useState<number | null>(null);
  const [syncStatus, setSyncStatus] = useState<Record<number, { ok: boolean; msg: string }>>({});

  const handleSync = async (calId: number) => {
    setSyncingId(calId);
    setSyncStatus(s => ({ ...s, [calId]: { ok: false, msg: '' } }));
    try {
      const result: CalendarSyncResult = await syncCalendar(apt.id, calId);
      setSyncStatus(s => ({
        ...s,
        [calId]: { ok: true, msg: `+${result.imported} new, ${result.updated} updated` },
      }));
    } catch (e: any) {
      setSyncStatus(s => ({
        ...s,
        [calId]: { ok: false, msg: e?.response?.data?.message || 'Sync failed' },
      }));
    } finally {
      setSyncingId(null);
    }
  };

  return (
  <div style={{
    border: `1px solid ${apt.isActive ? '#e2e8f0' : '#fed7d7'}`,
    borderRadius: 10,
    padding: '12px 14px',
    background: apt.isActive ? '#fff' : '#fff5f5',
    boxShadow: '0 1px 4px rgba(15,23,42,0.05)',
  }}>
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 8, marginBottom: 8 }}>
      <div>
        <div style={{ fontWeight: 700, fontSize: '0.95rem', color: '#2d3748' }}>{apt.name}</div>
        {apt.address && <div style={{ fontSize: '0.78rem', color: '#718096', marginTop: 2 }}>{apt.address}</div>}
        <div style={{ fontSize: '0.72rem', color: '#a0aec0', marginTop: 2 }}>
          Added {fmtDate(apt.createdAt)} · {apt.capacity ?? '?'} guests
        </div>
      </div>
      <span style={{
        flexShrink: 0,
        padding: '2px 10px',
        borderRadius: 99,
        fontSize: '0.72rem',
        fontWeight: 700,
        background: apt.isActive ? '#c6f6d5' : '#fed7d7',
        color: apt.isActive ? '#22543d' : '#9b2c2c',
        alignSelf: 'flex-start',
      }}>
        {apt.isActive ? 'Active' : 'Inactive'}
      </span>
    </div>

    {/* Stats row */}
    <div style={{
      display: 'flex',
      gap: 16,
      padding: '8px 0',
      borderTop: '1px solid #f0f4f8',
      borderBottom: '1px solid #f0f4f8',
      marginBottom: 10,
    }}>
      <StatPill label="reservations" value={apt.reservationCount} />
      <StatPill label="calendars" value={apt.calendarCount} />
    </div>

    {/* Calendar links with admin Resync */}
    {apt.calendars && apt.calendars.length > 0 && (
      <div style={{ marginBottom: 10 }}>
        {apt.calendars.map((cal: ApartmentCalendarInfo) => {
          const status = syncStatus[cal.id];
          const isSyncing = syncingId === cal.id;
          return (
            <div key={cal.id} style={{ marginBottom: 5 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.72rem', color: '#718096', minWidth: 60, flexShrink: 0 }}>{cal.name}</span>
                <a
                  href={cal.url}
                  target="_blank"
                  rel="noreferrer"
                  style={{ fontSize: '0.72rem', color: '#2b6cb0', wordBreak: 'break-all', flex: 1 }}
                >
                  {cal.url}
                </a>
                <button
                  onClick={() => handleSync(cal.id)}
                  disabled={syncingId !== null}
                  style={{
                    flexShrink: 0,
                    padding: '2px 8px',
                    fontSize: '0.68rem',
                    fontWeight: 600,
                    border: '1px solid #bee3f8',
                    borderRadius: 6,
                    background: isSyncing ? '#ebf8ff' : '#fff',
                    color: '#2b6cb0',
                    cursor: syncingId !== null ? 'not-allowed' : 'pointer',
                    opacity: syncingId !== null && !isSyncing ? 0.5 : 1,
                  }}
                >
                  {isSyncing ? '⏳' : '↻ Sync'}
                </button>
              </div>
              {status && status.msg && (
                <div style={{
                  fontSize: '0.68rem',
                  color: status.ok ? '#276749' : '#9b2c2c',
                  marginTop: 2,
                  paddingLeft: 66,
                }}>
                  {status.ok ? '✓' : '✗'} {status.msg}
                </div>
              )}
            </div>
          );
        })}
      </div>
    )}

    {/* Feature badges */}
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 10 }}>
      <FeatureBadge label="Guide" active={apt.guideEnabled} />
      <FeatureBadge label="Cleaning" active={apt.cleaningEnabled} />
      {apt.ttlockEnabled != null && <FeatureBadge label="Smart lock" active={!!apt.ttlockEnabled} />}
    </div>

    {/* Actions */}
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
      <Link
        to={`/admin/apartments/${apt.id}?returnTo=${encodeURIComponent(returnTo)}`}
        style={{ padding: '5px 12px', fontSize: '0.78rem', fontWeight: 600, borderRadius: 6, border: '1px solid #bee3f8', background: '#ebf8ff', color: '#2b6cb0', textDecoration: 'none', display: 'inline-block' }}
      >
        Manage →
      </Link>
      {apt.isActive ? (
        <button
          onClick={() => onDeactivate(apt.id)}
          style={{ padding: '5px 12px', fontSize: '0.78rem', fontWeight: 600, borderRadius: 6, border: '1px solid #fc8181', background: '#fff5f5', color: '#c53030', cursor: 'pointer' }}
        >
          Deactivate (Unpaid)
        </button>
      ) : (
        <button
          onClick={() => onActivate(apt.id)}
          style={{ padding: '5px 12px', fontSize: '0.78rem', fontWeight: 600, borderRadius: 6, border: '1px solid #68d391', background: '#f0fff4', color: '#22543d', cursor: 'pointer' }}
        >
          Activate (Paid)
        </button>
      )}
    </div>
  </div>
  );
};

type InfoRowProps = { label: string; value: React.ReactNode };
const InfoRow: React.FC<InfoRowProps> = ({ label, value }) => (
  <div style={{ display: 'flex', gap: 8, fontSize: '0.88rem', marginBottom: 5 }}>
    <span style={{ color: '#718096', minWidth: 110, flexShrink: 0 }}>{label}</span>
    <span style={{ color: '#2d3748', fontWeight: 500 }}>{value}</span>
  </div>
);

const AdminUserDetail: React.FC = () => {
  const { lang } = useLang();
  const resetText = adminPasswordResetText[lang];
  const { id } = useParams<{ id: string }>();
  const userId = Number(id);
  const navigate = useNavigate();

  const [user, setUser] = useState<UserAdmin | null>(null);
  const [managers, setManagers] = useState<ManagerAdmin[]>([]);
  const [apartments, setApartments] = useState<ApartmentAdmin[]>([]);
  const [loading, setLoading] = useState(false);
  const [impersonating, setImpersonating] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const managerId = useMemo(() => {
    const m = managers.find((x) => x.user?.id === userId);
    return m?.id;
  }, [managers, userId]);

  const load = async () => {
    if (!userId) return;
    setLoading(true);
    setError(null);
    try {
      const userData = await getUserById(userId, getToken());
      setUser(userData);

      // An ADMIN has no manager profile or apartments; do not go looking for them.
      if (userData.role === 'ADMIN') {
        setManagers([]);
        setApartments([]);
        return;
      }

      const managersData = await getAllManagers(getToken());
      setManagers(managersData);

      const m = managersData.find((x) => x.user?.id === userId);
      if (m) {
        const apts = await getApartmentsByManager(m.id, getToken());
        setApartments(apts);
      } else {
        setApartments([]);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load user detail');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [id]);

  const handleActivateApartment = async (apartmentId: number) => {
    try {
      await activateApartmentByAdmin(apartmentId, getToken());
      setSuccess('Apartment activated');
      load();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to activate apartment');
    }
  };

  const handleDeactivateApartment = async (apartmentId: number) => {
    try {
      await deactivateApartmentByAdmin(apartmentId, getToken());
      setSuccess('Apartment deactivated (billing hold applied)');
      load();
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to deactivate apartment');
    }
  };

  const handleImpersonate = async () => {
    if (!user) return;
    setImpersonating(true);
    setError(null);
    try {
      const result = await impersonateUser(userId, getToken());
      // Save full admin auth state so we can restore it on exit
      localStorage.setItem('adminToken', localStorage.getItem('token') || '');
      localStorage.setItem('adminUserEmail', localStorage.getItem('userEmail') || '');
      localStorage.setItem('adminUserRole', localStorage.getItem('userRole') || '');
      localStorage.setItem('adminUserId', localStorage.getItem('userId') || '');
      localStorage.setItem('adminReturnPath', '/admin/users/' + userId);
      // Switch to manager auth state
      localStorage.setItem('token', result.token);
      localStorage.setItem('userEmail', result.email);
      localStorage.setItem('userRole', result.role);
      localStorage.setItem('userId', String(userId));
      localStorage.setItem('impersonatingName', result.name.trim() || result.email);
      window.dispatchEvent(new Event('guestbook-auth-state-changed'));
      navigate('/');
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to impersonate user');
    } finally {
      setImpersonating(false);
    }
  };

  const isAdminTarget = user?.role === 'ADMIN';
  const activeCount = apartments.filter((a) => a.isActive).length;
  const totalReservations = apartments.reduce((s, a) => s + (a.reservationCount ?? 0), 0);
  const anyGuide = apartments.some((a) => a.guideEnabled);
  const anyCleaning = apartments.some((a) => a.cleaningEnabled);
  const anyTtlock = apartments.some((a) => a.ttlockEnabled);
  const anyCalendar = apartments.some((a) => (a.calendarCount ?? 0) > 0);

  return (
    <div style={{ maxWidth: 600, margin: '0 auto', padding: '16px 12px' }}>
      <div style={{ marginBottom: 12 }}>
        <Link to="/admin/users" style={{ color: '#4a5568', fontSize: '0.88rem' }}>← Back to Users</Link>
      </div>

      {loading && <div style={{ color: '#718096', padding: 20, textAlign: 'center' }}>Loading…</div>}
      {success && <div style={{ color: '#22543d', background: '#f0fff4', border: '1px solid #68d391', borderRadius: 6, padding: '8px 12px', marginBottom: 10, fontSize: '0.88rem' }}>{success}</div>}
      {error && <div style={{ color: '#9b2c2c', background: '#fff5f5', border: '1px solid #fc8181', borderRadius: 6, padding: '8px 12px', marginBottom: 10, fontSize: '0.88rem' }}>{error}</div>}

      {user && resetOpen && (
        <AdminPasswordResetModal
          target={user}
          onClose={() => setResetOpen(false)}
          onSuccess={(name) => { setError(null); setSuccess(fillTemplate(resetText.success, { name })); }}
        />
      )}

      {user && (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 10, padding: '14px 16px', marginBottom: 16, boxShadow: '0 1px 4px rgba(15,23,42,0.05)' }}>
          <div style={{ fontWeight: 700, fontSize: '1.05rem', color: '#2d3748', marginBottom: 10 }}>
            {user.firstName} {user.lastName}
          </div>
          <InfoRow label="Email" value={user.email} />
          {user.phoneNumber && <InfoRow label="Phone" value={user.phoneNumber} />}
          {user.ico && <InfoRow label="IČO" value={user.ico} />}
          <InfoRow label="Role" value={user.role} />
          <InfoRow label="Status" value={
            <span style={{ color: user.isActive ? '#22543d' : '#9b2c2c', fontWeight: 700 }}>
              {user.isActive ? 'Active' : 'Inactive'}
            </span>
          } />
          <InfoRow label="Email verified" value={
            <span style={{ color: user.emailConfirmed ? '#22543d' : '#9b2c2c' }}>
              {user.emailConfirmed ? 'Yes' : 'Pending'}
            </span>
          } />
          <InfoRow label="Registered" value={fmtDate(user.createdAt)} />
          {managerId && <InfoRow label="Manager ID" value={`#${managerId}`} />}

          {(user.plannedApartmentCount != null || user.preferredBillingCycle) && (
            <div style={{ marginTop: 10, paddingTop: 10, borderTop: '1px solid #f0f4f8' }}>
              <div style={{ fontSize: '0.78rem', fontWeight: 700, color: '#a0aec0', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Registration intent</div>
              {user.plannedApartmentCount != null && (
                <InfoRow label="Planned apts" value={user.plannedApartmentCount >= 20 ? '20+' : user.plannedApartmentCount} />
              )}
              {user.preferredBillingCycle && (
                <InfoRow label="Billing pref" value={user.preferredBillingCycle === 'ANNUAL' ? 'Annual (225 CZK/apt)' : 'Monthly (300 CZK/apt)'} />
              )}
            </div>
          )}

          {!isAdminTarget && (
          <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px solid #f0f4f8' }}>
            <button
              onClick={handleImpersonate}
              disabled={impersonating || !user.isActive}
              title={!user.isActive ? 'User is inactive' : 'Log in as this user'}
              style={{
                padding: '7px 16px', fontSize: '0.85rem', fontWeight: 600,
                border: '1px solid #d6bcfa', borderRadius: 8,
                background: '#faf5ff', color: '#6b46c1', cursor: 'pointer',
                opacity: (!user.isActive || impersonating) ? 0.5 : 1,
              }}
            >
              {impersonating ? '⏳ Přihlašuji…' : '👤 Přihlásit se jako tento uživatel'}
            </button>
            <div style={{ fontSize: '0.74rem', color: '#a0aec0', marginTop: 4 }}>
              Token platí 1 hodinu. Vrátit se: tlačítko v záhlaví.
            </div>
          </div>
          )}

          {user.passwordResetAllowed === true && (
            <div style={{ marginTop: 12 }}>
              <button
                type="button"
                onClick={() => { setSuccess(null); setResetOpen(true); }}
                style={{
                  padding: '7px 16px', fontSize: '0.85rem', fontWeight: 600,
                  border: '1px solid #fbd38d', borderRadius: 8,
                  background: '#fffaf0', color: '#c05621', cursor: 'pointer',
                }}
              >
                🔑 {resetText.button}
              </button>
            </div>
          )}
        </div>
      )}

      {!isAdminTarget && (
      <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
        <h3 style={{ margin: 0, fontSize: '1rem', color: '#2d3748' }}>
          Apartments ({apartments.length})
          {apartments.length > 0 && <span style={{ fontWeight: 400, color: '#a0aec0', fontSize: '0.82rem', marginLeft: 6 }}>{activeCount} active</span>}
        </h3>
        {apartments.length > 0 && (
          <div style={{ display: 'flex', gap: 10, fontSize: '0.78rem', color: '#718096' }}>
            <span title="Total reservations">📋 {totalReservations}</span>
          </div>
        )}
      </div>

      {/* Usage summary */}
      {apartments.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 14 }}>
          <FeatureBadge label="Check-in guide" active={anyGuide} />
          <FeatureBadge label="Cleaning" active={anyCleaning} />
          <FeatureBadge label="Calendar sync" active={anyCalendar} />
          <FeatureBadge label="Smart lock" active={anyTtlock} />
        </div>
      )}

      {!managerId ? (
        <div style={{ color: '#718096', fontSize: '0.88rem', padding: '12px 0' }}>No manager profile — no apartments.</div>
      ) : apartments.length === 0 ? (
        <div style={{ color: '#718096', fontSize: '0.88rem', padding: '12px 0' }}>No apartments yet.</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {apartments.map((a) => (
            <ApartmentCard
              key={a.id}
              apt={a}
              returnTo={`/admin/users/${userId}`}
              onActivate={handleActivateApartment}
              onDeactivate={handleDeactivateApartment}
            />
          ))}
        </div>
      )}
      </>
      )}
    </div>
  );
};

export default AdminUserDetail;
